<template>
  <panel title="Roll Call Summary" v-mask="maskOptions">
    <template #icons>
      <panel-header-icon icon="sync" @click="refresh" />
    </template>
    <div class="roll-call-list">
      <div class="roll-call-item">
        <div class="item-icon-container">
          <font-awesome-icon icon="bullhorn" class="item-icon" />
        </div>
        <div class="item-details">
          <Button v-if="workersPresentCount > 0" class="item-action-btn" label="Workers present" variant="link" @click="toggleWorkersPresent" />
          <span v-else class="item-label">Workers present</span>
          <span class="item-value">{{ workersPresentCount }}</span>
        </div>
      </div>
      <div v-if="costingEnabled" class="roll-call-item">
        <div class="item-icon-container">
          <font-awesome-icon icon="sitemap" class="item-icon" />
        </div>
        <div class="item-details">
          <Button v-if="jobsPresentCount > 0" class="item-action-btn" label="Jobs present" variant="link" @click="toggleJobsPresent" />
          <span v-else class="item-label">Jobs present</span>
          <span class="item-value">{{ jobsPresentCount }}</span>
        </div>
      </div>
    </div>
    <router-link :to="reportLink" class="btn-report-link">View full Roll Call report</router-link>
    <PVPopover ref="workerPop">
      <div class="popover-content tw:max-h-96 tw:overflow-y-auto">
        <div v-for="(worker, index) in workersPresent" :key="index" class="popover-item">
          {{ worker }}
        </div>
      </div>
    </PVPopover>
    <PVPopover ref="jobPop">
      <div class="popover-content">
        <Tree :value="jobTreeNodes" class="tw:w-full tw:p-0 tw:max-h-96 tw:overflow-y-auto" :pt="{
          nodeContent: { class: 'tw:p-0 tw:gap-0 tw:group' },
          nodeToggleButton: { class: ['tw:ml-0', 'tw:group-[.worker]:h-0'] }
        }">
          <template #job="slotProps">
            <router-link :to="getReportLinkForJob(slotProps.node.key)" class="tw:inline-block tw:text-indigo-400 hover:tw:underline">{{ slotProps.node.label }}</router-link>
          </template>
        </Tree>
        <router-link :to="jobReportLink" class="btn-report-link tw:mt-3 tw:inline-block">View Roll Call report by Job</router-link>
      </div>
    </PVPopover>
  </panel>
</template>

<script>
import { mapGetters } from 'vuex'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import Tree from 'primevue/tree'
import _ from 'lodash'
import AutoRefresh from '@/mixins/AutoRefresh'
import Maskable from '@/mixins/Maskable'
import Panel from '@/components/Panel.vue'
import PanelHeaderIcon from '@/components/PanelHeaderIcon.vue'
import service from '@/modules/attendance/reports/RollCallService'

export default {
  mixins: [AutoRefresh, Maskable],
  components: {
    Button,
    Panel,
    PanelHeaderIcon,
    PVPopover: Popover,
    Tree
  },
  data () {
    return {
      results: [],
      supplementalData: {},
      reportLink: {
        name: 'roll-call',
        params: {
          view: JSON.stringify({
            status: 'present'
          })
        }
      },
      jobReportLink: {
        name: 'roll-call',
        params: {
          view: JSON.stringify({
            status: 'present',
            group: ['jobName']
          })
        }
      }
    }
  },
  computed: {
    ...mapGetters(['costingEnabled']),
    ...mapGetters('formatPreferences', ['formatName']),
    presentResults () {
      return this.results.filter(r => r.present)
    },
    workersPresentCount () {
      return this.presentResults.length
    },
    jobsPresent () {
      return new Set(this.presentResults.flatMap(r => r.job ? [r.job] : []))
    },
    jobsPresentCount () {
      return this.jobsPresent.size
    },
    workersPresent () {
      return this.presentResults.map(r => this.formatName(r.firstName, r.lastName, r.middleName, r.displayName)).sort()
    },
    jobTreeNodes () {
      const presentByJob = Object.entries(_.groupBy(this.presentResults, 'job')).flatMap(([job, present]) => {
        const jobName = this.supplementalData.job?.[job]?.name ?? ''
        if (!jobName) return [] // no job would have had "null" job key
        return [{
          key: job,
          label: `${jobName} (${present.length})`,
          type: 'job',
          children: _.orderBy(present.map(p => ( { key: p.employee, label: this.formatName(p.firstName, p.lastName, p.middleName, p.displayName), styleClass: 'worker' })), 'label')
        }]
      })
      return _.orderBy(presentByJob, 'label')
    }
  },
  methods: {
    load () {
      this.showLoadingMask()
      return service.list({ status: 'present' })
        .then(data => {
          this.results = data.results
          this.supplementalData = data.supplementalData
          this.hideMask()
        })
        .catch(error => {
          this.showErrorMask(error)
          return Promise.reject(error)
        })
    },
    refreshImpl () {
      return this.load()
    },
    refresh () {
      this.coordinateWithRefresh(() => this.load())
    },
    toggleWorkersPresent (event) {
      this.$refs.workerPop.toggle(event)
    },
    toggleJobsPresent (event) {
      this.$refs.jobPop.toggle(event)
    },
    getReportLinkForJob (jobId) {
      return {
        name: 'roll-call',
        params: {
          view: JSON.stringify({
            status: 'present',
            job: [parseInt(jobId)]
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.roll-call-list {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.5rem;
}

.roll-call-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.65rem;
  background-color: var(--color-slate-955);
  border: 1px solid var(--color-slate-800);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.roll-call-item:hover {
  background-color: var(--color-slate-850);
  border-color: var(--color-indigo-500);
}

.item-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  background-color: rgba(26, 95, 210, 0.15);
  color: var(--color-indigo-500);
  border-radius: 6px;
  font-size: 0.95rem;
}

.item-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
}

.item-action-btn {
  padding: 0 !important;
  color: var(--color-indigo-500) !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  border: none !important;
  background: transparent !important;
  cursor: pointer;
  text-decoration: none;
  margin-right: 0.75rem;
}

.item-action-btn:hover {
  color: var(--color-indigo-400) !important;
  text-decoration: underline !important;
}

.item-label {
  color: var(--color-slate-300);
  font-size: 0.85rem;
  font-weight: 500;
  margin-right: 0.75rem;
}

.item-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-slate-100);
}

.btn-report-link {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-indigo-500);
  text-decoration: none;
  transition: color 0.2s;
  margin-left: 0.25rem;
}

.btn-report-link:hover {
  color: var(--color-indigo-400);
  text-decoration: underline;
}

.popover-content {
  padding: 0.4rem;
}

.popover-item {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: var(--color-slate-300);
  border-bottom: 1px solid var(--color-slate-800);
}

.popover-item:last-child {
  border-bottom: none;
}
</style>

